import React, { FC, useState } from 'react'
import {
  Box,
  Stack,
  Typography,
  Button,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import { AITranscribeResult } from '../apis/prescription'

interface AITranscribePanelProps {
  result: AITranscribeResult | null
  isLoading: boolean
  error: string
  onTranscribe: () => void
}

// コピーボタン付きの値表示行
const CopyableRow: FC<{ label: string; value: string }> = ({
  label,
  value,
}) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    if (!value) return
    await navigator.clipboard.writeText(value)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 0.5,
        px: 1,
        borderRadius: 1,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: value ? 'text.primary' : 'text.disabled',
          }}
        >
          {value || '（読み取れませんでした）'}
        </Typography>
      </Box>
      {value && (
        <Tooltip title={copied ? 'コピーしました' : 'コピー'}>
          <IconButton size="small" onClick={handleCopy} sx={{ ml: 1 }}>
            {copied ? (
              <CheckIcon fontSize="small" color="success" />
            ) : (
              <ContentCopyIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  )
}

// セクションカード
const Section: FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="subtitle2"
      sx={{ mb: 0.5, fontWeight: 700, color: 'primary.main' }}
    >
      {title}
    </Typography>
    <Paper variant="outlined" sx={{ p: 1 }}>
      <Stack spacing={0}>{children}</Stack>
    </Paper>
  </Box>
)

export const AITranscribePanel: FC<AITranscribePanelProps> = ({
  result,
  isLoading,
  error,
  onTranscribe,
}) => {
  return (
    <Paper sx={{ p: 2, height: '100%', overflow: 'auto' }}>
      <Typography variant="h6" gutterBottom>
        AI書き起こし
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={
            isLoading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <AutoAwesomeIcon fontSize="small" />
            )
          }
          onClick={onTranscribe}
          disabled={isLoading}
          sx={{
            px: 4,
            py: 1,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            boxShadow: 2,
            '&:hover': { boxShadow: 4 },
          }}
        >
          {isLoading ? '書き起こし中...' : 'AI書き起こし実行'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {result && (
        <>
          <Divider sx={{ mb: 2 }} />

          <Section title="医療機関フォーマット">
            <CopyableRow label="性別" value={result.format.sex} />
            <CopyableRow
              label="保険区分"
              value={result.format.insurance_type}
            />
            <CopyableRow label="生年月日" value={result.format.birthday} />
            <CopyableRow
              label="診療科領域"
              value={result.format.department_area}
            />
            <CopyableRow
              label="医薬品表記"
              value={result.format.medicine_layout}
            />
            <CopyableRow label="用量表記" value={result.format.dosage} />
            <CopyableRow
              label="調剤数量"
              value={result.format.dispensing_quantity}
            />
          </Section>

          <Section title="診療科">
            <CopyableRow label="診療科名" value={result.department.name} />
          </Section>

          <Section title="医療機関情報">
            <CopyableRow
              label="統一医療機関コード"
              value={result.medical_institution.unified_code}
            />
            <CopyableRow
              label="記載住所"
              value={result.medical_institution.address}
            />
            <CopyableRow
              label="記載医療機関名"
              value={result.medical_institution.name}
            />
          </Section>

          <Section title="患者情報">
            <CopyableRow
              label="患者氏名（漢字）"
              value={result.patient.name_kanji}
            />
            <CopyableRow
              label="患者氏名（カナ）"
              value={result.patient.name_kana}
            />
          </Section>

          <Section title="医師情報">
            <CopyableRow
              label="医師氏名（漢字）"
              value={result.doctor.name_kanji}
            />
            <CopyableRow
              label="医師氏名（カナ）"
              value={result.doctor.name_kana}
            />
          </Section>
        </>
      )}
    </Paper>
  )
}
